import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { createComment, deleteComment, fetchComments, resolveComment } from "../api/productTablesApi";
import { getSelectedBoqSheetRow, readBoqTable } from "../excel/boq-table";

const box = { background: "var(--surface-soft)", border: "1px solid var(--surface-border)", borderRadius: 10, padding: "8px 10px" };

export default function BoqCommentsPanel({
  isOpen, onClose, token, projectId, versionId, sheetName, canEdit, canModerate, myUserId, onChanged,
}) {
  const [comments, setComments] = useState([]);
  const [ref, setRef] = useState("");
  const [text, setText] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token || !projectId || !versionId) return;
    try {
      const list = await fetchComments(token, projectId, versionId);
      setComments(list || []);
      if (onChanged) onChanged((list || []).filter((c) => !c.resolved).length);
    } catch (e) {
      setError(e.message || "Failed to load comments");
    }
  }, [token, projectId, versionId, onChanged]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const useSelectedRow = async () => {
    setError("");
    try {
      const row = await getSelectedBoqSheetRow(sheetName);
      const rows = await readBoqTable(sheetName);
      const hit = rows.find((r) => r.sheetRow === row);
      if (!hit) throw new Error("აირჩიე უჯრა TOTAL ფურცლის სტრიქონში.");
      setRef(hit.number || "");
    } catch (e) {
      setError(e.message || "Unable to read the selected row");
    }
  };

  const add = async () => {
    if (!ref.trim() || !text.trim()) return;
    setBusy(true);
    setError("");
    try {
      await createComment(token, projectId, { version_id: versionId, ref: ref.trim(), text: text.trim() });
      setText("");
      await load();
    } catch (e) {
      setError(e.message || "Failed to add comment");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c) => {
    try { await resolveComment(token, projectId, c.id, !c.resolved); await load(); }
    catch (e) { setError(e.message); }
  };
  const remove = async (c) => {
    try { await deleteComment(token, projectId, c.id); await load(); }
    catch (e) { setError(e.message); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    comments.filter((c) => showResolved || !c.resolved).forEach((c) => {
      if (!map.has(c.ref)) map.set(c.ref, []);
      map.get(c.ref).push(c);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [comments, showResolved]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      contentLabel="Comments"
      style={{
        overlay: { backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1000 },
        content: {
          inset: "5% 4%", padding: 0, border: "1px solid var(--surface-border)", borderRadius: 14,
          background: "var(--page-gradient)", color: "var(--text-primary)", overflow: "hidden", display: "flex", flexDirection: "column",
        },
      }}
    >
      <div data-no-translate="true" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: "1px solid var(--surface-border)" }}>
          <strong>💬 კომენტარები</strong>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "inherit", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {error && <div className="msg-error" style={{ margin: 10 }}>{error}</div>}

        {canEdit && (
          <div style={{ padding: 12, borderBottom: "1px solid var(--surface-border)" }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input
                value={ref} onChange={(e) => setRef(e.target.value)} placeholder="სტრიქონი (მაგ. 2.1)"
                style={{ width: 130, padding: "6px 8px", borderRadius: 8, fontSize: 12 }}
              />
              <button type="button" onClick={useSelectedRow} style={{ padding: "6px 10px", borderRadius: 8, fontSize: 12, cursor: "pointer", border: "1px solid var(--surface-border)", background: "var(--surface-soft)", color: "var(--text-primary)" }}>
                არჩეული სტრიქონი
              </button>
            </div>
            <textarea
              value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000}
              placeholder="კომენტარი (მაგ. ფასი დასაზუსტებელია მომწოდებელთან)"
              style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 8, fontSize: 12, resize: "vertical" }}
            />
            <button className="primary" onClick={add} disabled={busy || !ref.trim() || !text.trim()} style={{ marginTop: 6, padding: "6px 14px" }}>
              დამატება
            </button>
          </div>
        )}

        <div style={{ padding: "8px 12px", fontSize: 12, display: "flex", justifyContent: "space-between", color: "var(--text-muted)" }}>
          <span>{comments.filter((c) => !c.resolved).length} გადაუჭრელი</span>
          <label style={{ cursor: "pointer" }}>
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> გადაჭრილიც
          </label>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {grouped.length === 0 && <div style={{ ...box, textAlign: "center", color: "var(--text-muted)", fontSize: 12 }}>კომენტარი არ არის</div>}
          {grouped.map(([r, list]) => (
            <div key={r}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent-primary)", marginBottom: 4 }}>სტრიქონი {r}</div>
              {list.map((c) => (
                <div key={c.id} style={{ ...box, marginBottom: 4, opacity: c.resolved ? 0.6 : 1 }}>
                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap", textDecoration: c.resolved ? "line-through" : "none" }}>{c.text}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, fontSize: 11, color: "var(--text-muted)" }}>
                    <span style={{ flex: 1 }}>{c.author_name || "—"} · {c.created_at ? new Date(c.created_at).toLocaleString("ka-GE") : ""}</span>
                    {canEdit && (
                      <button onClick={() => toggle(c)} style={{ background: "none", border: "none", color: "#34d399", cursor: "pointer", fontSize: 11 }}>
                        {c.resolved ? "↺ გახსნა" : "✓ გადაჭრა"}
                      </button>
                    )}
                    {(canModerate || c.author_id === myUserId) && (
                      <button onClick={() => remove(c)} style={{ background: "none", border: "none", color: "#fb7185", cursor: "pointer", fontSize: 11 }}>წაშლა</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
